import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LoginModal } from './LoginModal';

interface DisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorName: string;
  onLoginSuccess?: () => void;
}

export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ 
  isOpen, 
  onClose, 
  tutorName,
  onLoginSuccess 
}) => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);

  if (!isOpen) return null;

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  const handleSignup = () => {
    onClose();
    navigate('/signup');
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    onClose();
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  const modalContent = (
    <>
      {showLoginModal ? (
        <LoginModal 
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          tutorName={tutorName}
          onSuccess={handleLoginSuccess}
        />
      ) : (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative animate-in fade-in duration-300">
            {/* Bouton fermer - plus visible */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Fermer"
            >
              <i className="fas fa-times text-sm"></i>
            </button>

            {/* Header - plus moderne */}
            <div className="text-center mb-8">
              <div className="mx-auto h-20 w-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                <i className="fas fa-user-lock text-white text-3xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Connexion requise
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Pour contacter <span className="font-semibold text-blue-600">{tutorName}</span>, 
                vous devez être connecté à votre compte.
              </p>
            </div>

            {/* Benefits - design amélioré */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                Avec un compte, vous pouvez :
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <i className="fas fa-comments text-green-600 text-xl mb-2"></i>
                  <p className="text-sm font-medium text-gray-800">Contacter directement</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <i className="fas fa-address-book text-blue-600 text-xl mb-2"></i>
                  <p className="text-sm font-medium text-gray-800">Infos de contact</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg text-center">
                  <i className="fas fa-star text-yellow-600 text-xl mb-2"></i>
                  <p className="text-sm font-medium text-gray-800">Noter les tuteurs</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <i className="fas fa-calendar-check text-purple-600 text-xl mb-2"></i>
                  <p className="text-sm font-medium text-gray-800">Gérer vos demandes</p>
                </div>
              </div>
            </div>

            {/* Actions - design amélioré */}
            <div className="space-y-4">
              <button
                onClick={handleLogin}
                className="w-full py-4 px-6 bg-[#002c46] text-white font-bold rounded-xl hover:bg-[#001a2a] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                <i className="fas fa-sign-in-alt mr-3 text-lg"></i>
                Se connecter
              </button>
              <button
                onClick={handleSignup}
                className="w-full py-4 px-6 border-2 border-[#002c46] text-[#002c46] font-bold rounded-xl hover:bg-[#002c46]/5 transition-all duration-200"
              >
                <i className="fas fa-user-plus mr-3 text-lg"></i>
                Créer un compte
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 px-4 text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium"
              >
                <i className="fas fa-times mr-2"></i>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Utiliser createPortal pour afficher le modal au niveau du document
  return isOpen ? createPortal(modalContent, document.body) : null;
};